import { useMutation } from "@apollo/client";
import { useStripe } from "@stripe/react-stripe-js";
import { Header, Paragraph } from "@datagame/ui";

import { STRIPE_CHECKOUT_CREATE } from "../../graphql/mutations/UserMutations";
import { PlanName } from "../../gql/graphql";
import { useTranslation } from "react-i18next";
import { Plan } from "../../components/subscription/Plan";

import styles from "./ActivatePage.module.scss";

const STRIPE_BASIC_PLAN_ID = import.meta.env.VITE_STRIPE_BASIC_PLAN_ID;
const STRIPE_CORE_PLAN_ID = import.meta.env.VITE_STRIPE_CORE_PLAN_ID;
const STRIPE_PROFESSIONAL_PLAN_ID = import.meta.env.VITE_STRIPE_PROFESSIONAL_PLAN_ID;
const STRIPE_TEAMS_PLAN_ID = import.meta.env.VITE_STRIPE_TEAMS_PLAN_ID;

export const plans: Plan[] = [
  {
    id: "basic",
    name: PlanName.Basic,
    price: "$40",
    priceId: STRIPE_BASIC_PLAN_ID,
    features: [
      { name: "Include all game types" },
      { name: "Publish to hosted URL" },
      { name: "Export data to Excel" },
      { name: "Email support" },
    ],
  },
  {
    id: "core",
    name: PlanName.Core,
    price: "$99",
    priceId: STRIPE_CORE_PLAN_ID,
    features: [
      { name: "All Basic features", style: "bold" },
      { name: "Publish to Alchemer" },
      { name: "Use your own artwork" },
    ],
  },
  {
    id: "professional",
    name: PlanName.Professional,
    price: "$199",
    priceId: STRIPE_PROFESSIONAL_PLAN_ID,
    features: [
      { name: "All Core features", style: "bold" },
      { name: "Remove Datagame logo" },
      { name: "Phone Support" },
    ],
  },
  {
    id: "teams",
    name: PlanName.Teams,
    price: "$250",
    priceId: STRIPE_TEAMS_PLAN_ID,
    features: [
      { name: "All Professional features", style: "bold" },
      { name: "Three users included" },
      { name: "Invite additional users" },
    ],
  },
];

export interface Feature {
  name: string;
  style?: "bold";
}

export interface Plan {
  id: string;
  name: string;
  price: string;
  priceId: string;
  features: Feature[];
}

export const ActivatePage = () => {
  const { t } = useTranslation();
  const [createCheckoutSession] = useMutation(STRIPE_CHECKOUT_CREATE);
  const stripe = useStripe();

  const handleClick = async (plan: Plan) => {
    if (!stripe) {
      return;
    }

    createCheckoutSession({
      variables: { priceId: plan.priceId },
      onCompleted: async (data) => {
        await stripe.redirectToCheckout({
          sessionId: data.stripeCheckoutCreate.id,
        });
      },
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.subscriptionContainer}>
        <div className={styles.headerWrapper}>
          <Header variant="h6" color="secondary" className={styles.selectHeader}>
            {t("subscription:selectSubscription")}
          </Header>

          <Header variant="h5" className={styles.chooseHeader}>
            {t("subscription:chooseYour")} <span className={styles.orange}>{t("subscription:plan")}</span>
          </Header>

          <Paragraph size="small" color="secondary">
            {t("subscription:description")}
          </Paragraph>
        </div>

        <div className={styles.plans}>
          {plans.map((plan, index) => (
            <Plan key={index} plan={plan} onClick={handleClick} />
          ))}
        </div>
      </div>
    </div>
  );
};
